
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { lockService } from '../services/lockService';
import { User, Mail, Shield, Zap, Trash2, AlertTriangle, LogOut, ArrowLeft, BarChart3 } from 'lucide-react';
import { Button } from './ui/Button';

export const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState({ locks: 0, leads: 0, views: 0 });
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }
      setUser(user);

      // Load simple stats
      const locks = await lockService.getUserLocks(user.id);
      const totalViews = locks.reduce((acc, l) => acc + (l.viewsCount || 0), 0);
      const totalLeads = locks.reduce((acc, l) => acc + (l.leadsCount || 0), 0);
      
      setStats({
        locks: locks.length,
        leads: totalLeads,
        views: totalViews
      });
      setLoading(false);
    };

    loadData();
  }, [navigate]);

  const handleDeleteAccount = async () => {
      const confirmText = prompt("Type 'DELETE' to confirm account deletion. This will remove all your locks and leads immediately.");
      if (confirmText === 'DELETE' && user) {
          setIsDeleting(true);
          try {
              // 1. Delete all user locks (Cascade should handle leads usually, but being safe)
              // Note: Requires RLS policy to allow users to delete their own rows
              const locks = await lockService.getUserLocks(user.id);
              for (const lock of locks) {
                  await lockService.deleteLock(lock.id);
              }
              
              // 2. Sign out
              await supabase.auth.signOut();
              alert("Your data has been deleted.");
              navigate('/');
          } catch (error) {
              alert("Error deleting data. Please contact support.");
          } finally {
              setIsDeleting(false);
          }
      }
  };

  if (loading) return <div className="min-h-screen bg-slate-50" />;

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-20">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-slate-100 rounded-full">
                    <ArrowLeft className="w-5 h-5 text-slate-600" />
                </button>
                <h1 className="text-lg font-bold text-slate-900">Account Settings</h1>
            </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        
        {/* Identity Card */}
        <div className="bg-white p-6 rounded-[24px] border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center text-white text-xl font-bold">
                {user.email?.charAt(0).toUpperCase()}
            </div>
            <div>
                <h2 className="text-xl font-bold text-slate-900">My Profile</h2>
                <div className="flex items-center text-slate-500 text-sm mt-1">
                    <Mail className="w-3.5 h-3.5 mr-1.5" /> {user.email}
                </div>
            </div>
        </div>

        {/* Plan Card */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-[24px] text-white relative overflow-hidden">
            <div className="relative z-10">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <div className="text-slate-400 text-sm font-medium uppercase tracking-wider mb-1">Current Plan</div>
                        <div className="text-3xl font-bold">Beta Free</div>
                    </div>
                    <div className="bg-[#10B981] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5 shadow-lg shadow-green-900/20">
                        <Zap className="w-3 h-3 fill-current" /> Active
                    </div>
                </div>
                <p className="text-slate-300 text-sm max-w-md">
                    You are an early adopter of Lockd.in. You have unlimited access to all features during the beta period.
                </p>
            </div>
            {/* Decorative */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-16 -mt-16"></div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-[20px] border border-slate-200 text-center">
                <div className="text-2xl font-bold text-slate-900">{stats.locks}</div>
                <div className="text-xs text-slate-500 font-medium uppercase mt-1">Locks</div>
            </div>
            <div className="bg-white p-4 rounded-[20px] border border-slate-200 text-center">
                <div className="text-2xl font-bold text-slate-900">{stats.leads}</div>
                <div className="text-xs text-slate-500 font-medium uppercase mt-1">Leads</div>
            </div>
            <div className="bg-white p-4 rounded-[20px] border border-slate-200 text-center">
                <div className="text-2xl font-bold text-slate-900">{stats.views}</div>
                <div className="text-xs text-slate-500 font-medium uppercase mt-1">Views</div>
            </div>
        </div>

        {/* Danger Zone */}
        <div className="border border-red-100 bg-red-50/50 rounded-[24px] p-6">
            <h3 className="text-red-900 font-bold flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5" /> Danger Zone
            </h3>
            <div className="bg-white rounded-xl p-4 border border-red-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                    <div className="text-sm font-bold text-slate-900">Delete Account</div>
                    <div className="text-xs text-slate-500">Permanently remove your account and all data.</div>
                </div>
                <Button 
                    variant="secondary" 
                    onClick={handleDeleteAccount} 
                    isLoading={isDeleting}
                    className="bg-red-50 text-red-600 border-red-100 hover:bg-red-100 hover:border-red-200 whitespace-nowrap"
                >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Data
                </Button>
            </div>
        </div>

        <div className="flex justify-center pt-8">
             <button onClick={async () => { await supabase.auth.signOut(); navigate('/'); }} className="text-slate-400 hover:text-slate-900 text-sm font-medium flex items-center transition-colors">
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
             </button>
        </div>

      </main>
    </div>
  );
};
